// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import { UserRole } from "graphql/api";
import { ActionType, IAction, IActionSortTable, raiseAction } from "actions";
import { ICloudParameter, IModule, IOutput, IVirtAssign } from "parameter";
import { IState } from "states/states";
import { IStateDataOutput } from "states/datastates";
import { ITableSort } from "states/guistates";
import { makeSortHeader } from "guiutils";
import { isSet } from "utils";

import Output from "view/comp/Output";
import SearchFilter from "view/comp/SearchFilter";

// sort key of table
const TABLE_NAME = "t_outputs";

interface IOutputListExtProps {
  isEditable: boolean;
  isDashboard?: boolean;
}

interface IOutputListProps {
  outputs: IOutput[];
  values: IStateDataOutput[];
  virtualOutputs: IVirtAssign[];
  tableSort: ITableSort | undefined;
  showModules: boolean;
  modules: IModule[] | undefined;
}

interface IOutputListState {
  filterText: string;
}

const mapStateToProps = (state: IState, ownProps: IOutputListExtProps) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const showModules =
    !ownProps.isDashboard &&
    Boolean(curUser?.accessRights.controllerAdmin) &&
    Boolean(state.parameter?.module.length);

  const props: IOutputListProps = {
    outputs: state.parameter?.output ?? [],
    values: state.data.output,
    virtualOutputs: (state.parameter as ICloudParameter | undefined)?.virtualSite?.output ?? [],
    tableSort: state.gui.tableSort[TABLE_NAME],
    showModules,
    modules: state.parameter?.module,
  };
  return props;
};

interface IOutputListDispatch {
  onSort: (sort: IActionSortTable) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IOutputListDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  placeholderSearchFiled: {
    id: "Search for outputs...",
  },
});

class OutputListComponent extends React.Component<
  IOutputListProps & IOutputListExtProps & IOutputListDispatch & WrappedComponentProps
> {
  state: IOutputListState = {
    filterText: "",
  };

  private getSortKey(index: number) {
    const sortCol = this.props.tableSort?.column as keyof IOutput | "value" | undefined;
    return sortCol === "value"
      ? this.props.outputs[index].mode * 100 + this.props.values[index].logical
      : sortCol === "module"
      ? this.props.modules?.find((curItem) => curItem.id === this.props.outputs[index].module)?.name
      : this.props.outputs[index][sortCol ?? "name"];
  }

  public render() {
    const outputIds: number[] = [];
    this.props.outputs.forEach((curItem, curIndex) => {
      if (curItem.webActive) outputIds.push(curIndex);
    });
    const filterTextLowerCase = this.state.filterText.toLocaleLowerCase();
    const outputItems = outputIds
      .filter((output) => {
        return this.props.outputs[output].name.toLocaleLowerCase().includes(filterTextLowerCase);
      })
      .sort((a, b) => {
        const keyA = this.getSortKey(a);
        const keyB = this.getSortKey(b);
        const sort =
          (isSet(keyA) && isSet(keyB) && keyA > keyB) || (isSet(keyA) && !isSet(keyB)) ? 1 : -1;
        return this.props.tableSort?.isSortAscending !== false ? sort : -sort;
      })
      .map((curId) => {
        return (
          <Output
            key={curId}
            index={curId}
            isEditable={this.props.isEditable}
            showModule={this.props.showModules}
          />
        );
      });

    return (
      <div id="outputs">
        {this.props.isDashboard !== true && (
          <div className="tableSearchHeader">
            <h1>
              <FormattedMessage id="Outputs" />
            </h1>
            <SearchFilter
              className="tableSearchBox"
              filterText={this.state.filterText}
              placeholder={this.props.intl.formatMessage(messages.placeholderSearchFiled)}
              onChange={(filterText) => this.setState({ filterText })}
            />
          </div>
        )}
        {outputItems.length ? (
          <table className="datatable fullwidthwhite">
            <thead>
              <tr>
                {makeSortHeader(
                  TABLE_NAME,
                  "name",
                  <FormattedMessage id="Name" />,
                  this.props.tableSort ?? { column: "name", isSortAscending: true },
                  this.props.onSort,
                )}
                {this.props.showModules &&
                  makeSortHeader(
                    TABLE_NAME,
                    "module",
                    <FormattedMessage id="Module" />,
                    this.props.tableSort,
                    this.props.onSort,
                    ["optional"],
                  )}
                {makeSortHeader(
                  TABLE_NAME,
                  "value",
                  <FormattedMessage id="Value" />,
                  this.props.tableSort,
                  this.props.onSort,
                  ["value-col", "right"],
                  undefined,
                  2,
                )}
                {this.props.isEditable && <th></th>}
              </tr>
            </thead>
            <tbody>{outputItems}</tbody>
          </table>
        ) : (
          <p className="alert alert-warning">
            <FormattedMessage id="No outputs present." />
          </p>
        )}
      </div>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(OutputListComponent));
